/**
 * Persistencia local de escenarios de la calculadora ISR.
 *
 * Se guardan en `localStorage` bajo la key `axorial.calc.scenarios.v1`. La
 * versión va en la key para que cambios futuros de shape no requieran lógica
 * de migración: el v1 se ignora y queda como dato muerto.
 *
 * Cada escenario contiene su snapshot de resultado para que la tabla
 * comparativa no requiera recalcular contra el API — los datos reales
 * pueden cambiar entre guardado y comparación, y queremos ver el escenario
 * "tal como estaba". El botón "Recalcular" del row refresca contra hoy.
 */
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "axorial.calc.scenarios.v1";

export interface Hypo {
  id: string;
  kind: "ingreso" | "gasto";
  subtotal: number;
  iva: number;
  paymentDate: string;
  facturado: boolean;
  projectId: string;
}

export interface CashFlow {
  ingresosCobradosCents: number;
  gastosPagadosCents: number;
  baseIsrCents: number;
  isrCents: number;
  ivaTrasladadoCents: number;
  ivaAcreditableCents: number;
  ivaSaldoCents: number;
}

export interface SimResponse {
  baseline: CashFlow;
  withHypothetical: CashFlow;
  delta: { isrCents: number; ivaSaldoCents: number; netCashCents: number };
  droppedOutsidePeriod?: number[];
  note?: string;
}

export interface Scenario {
  id: string;
  name: string;
  /** Unix-seconds, igual que el resto del panel. */
  createdAt: number;
  /** Período YYYY-MM al que aplica el escenario. */
  period: string;
  hypos: Hypo[];
  result: SimResponse;
}

function loadFromStorage(): Scenario[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as Scenario[]) : [];
  } catch {
    return [];
  }
}

function saveToStorage(scenarios: Scenario[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(scenarios));
  } catch {
    // Quota excedida o navegador en modo privado — fallar silenciosamente.
  }
}

export function useScenarios() {
  const [scenarios, setScenarios] = useState<Scenario[]>(() => loadFromStorage());

  // Sync entre pestañas: si el usuario guarda en otra tab, refrescamos aquí.
  useEffect(() => {
    if (typeof window === "undefined") return;
    function onStorage(e: StorageEvent) {
      if (e.key === STORAGE_KEY) setScenarios(loadFromStorage());
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const add = useCallback((input: Omit<Scenario, "id" | "createdAt">): Scenario => {
    const full: Scenario = {
      ...input,
      id: crypto.randomUUID(),
      createdAt: Math.floor(Date.now() / 1000),
    };
    setScenarios((prev) => {
      const next = [...prev, full];
      saveToStorage(next);
      return next;
    });
    return full;
  }, []);

  const remove = useCallback((id: string) => {
    setScenarios((prev) => {
      const next = prev.filter((s) => s.id !== id);
      saveToStorage(next);
      return next;
    });
  }, []);

  const rename = useCallback((id: string, name: string) => {
    setScenarios((prev) => {
      const next = prev.map((s) => (s.id === id ? { ...s, name } : s));
      saveToStorage(next);
      return next;
    });
  }, []);

  const update = useCallback((id: string, patch: Partial<Scenario>) => {
    setScenarios((prev) => {
      const next = prev.map((s) => (s.id === id ? { ...s, ...patch } : s));
      saveToStorage(next);
      return next;
    });
  }, []);

  return { scenarios, add, remove, rename, update };
}
